import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import ConfigurableTable, { TableColumn, ColumnFilter } from '../common/ConfigurableTable';
import { useDebouncedCallback } from '../../hooks/useDebounced';
import EnhancedSearchInput from '../common/EnhancedSearchInput';

interface Device {
  id: string;
  customerId: string;
  model: string;
  serialNumber: string;
  warrantyExpires: string | null;
  invoiceNumber: string | null;
  invoiceDate: string | null;
  country: string | null;
  comments: string | null;
  customerName: string;
  customerEmail: string;
  customerCountry: string | null;
  companyName: string;
  ticketCount: number;
  serviceCount: number;
  lastTicketDate: string | null;
  linkedTickets: Array<{
    id: string;
    ticketNumber: string;
    title: string;
    status: string;
    createdAt: string;
  }>;
  createdAt: string;
  updatedAt: string;
}

interface DeviceListProps {
  onDeviceSelect: (device: Device) => void;
}

const DeviceList: React.FC<DeviceListProps> = ({ onDeviceSelect }) => {
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [modelFilter, setModelFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState<ColumnFilter[]>([]);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Sorting state
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const deviceModels = ['BWIII', 'BWMini', 'Compass', 'Maxxi'];

  // Load user preferences from localStorage
  const loadPreferences = () => {
    const storageKey = 'device-list-preferences';
    const defaultPrefs = {
      itemsPerPage: 50,
      sortBy: 'created_at',
      sortOrder: 'desc',
      searchTerm: '',
      modelFilter: '',
      statusFilter: '',
    };

    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          itemsPerPage: parsed.itemsPerPage || defaultPrefs.itemsPerPage,
          sortBy: parsed.sortBy || defaultPrefs.sortBy,
          sortOrder: parsed.sortOrder || defaultPrefs.sortOrder,
          searchTerm: parsed.searchTerm || defaultPrefs.searchTerm,
          modelFilter: parsed.modelFilter || defaultPrefs.modelFilter,
          statusFilter: parsed.statusFilter || defaultPrefs.statusFilter,
        };
      }
    } catch (error) {
      console.error('Error loading device preferences:', error);
    }
    return defaultPrefs;
  };

  // Save user preferences to localStorage
  const savePreferences = useCallback((preferences: any) => {
    try {
      const storageKey = 'device-list-preferences';
      localStorage.setItem(storageKey, JSON.stringify(preferences));
    } catch (error) {
      console.error('Error saving device preferences:', error);
    }
  }, []);

  // Debounced save preferences
  const debouncedSavePreferences = useDebouncedCallback(
    (preferences: any) => {
      savePreferences(preferences);
    },
    1000,
    [savePreferences]
  );

  // Stable search handler to prevent re-renders and focus loss
  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  // Auto-save preferences when they change
  useEffect(() => {
    debouncedSavePreferences({
      itemsPerPage,
      sortBy,
      sortOrder,
      searchTerm,
      modelFilter,
      statusFilter,
    });
  }, [itemsPerPage, sortBy, sortOrder, searchTerm, modelFilter, statusFilter, debouncedSavePreferences]);

  const loadDevices = useDebouncedCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const filters = {
        search: searchTerm,
        model: modelFilter,
        status: statusFilter,
        sortBy,
        sortOrder,
        page: currentPage,
        limit: itemsPerPage,
      };

      const result = await api.getDevices(filters);
      setDevices(result.devices);
      setTotalItems(result.pagination.total);
      setTotalPages(result.pagination.totalPages);
      setCurrentPage(result.pagination.page);
    } catch (err) {
      console.error('Error loading devices:', err);
      setError(err instanceof Error ? err.message : 'Failed to load devices');
    } finally {
      setLoading(false);
    }
  }, 800, []); // Increased delay and removed dependencies to make it stable

  // Track if preferences have been loaded
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);

  // Load preferences on mount
  useEffect(() => {
    const preferences = loadPreferences();
    setItemsPerPage(preferences.itemsPerPage);
    setSortBy(preferences.sortBy);
    setSortOrder(preferences.sortOrder);
    setSearchTerm(preferences.searchTerm);
    setModelFilter(preferences.modelFilter);
    setStatusFilter(preferences.statusFilter);
    setPreferencesLoaded(true);
  }, []);

  // Load devices when filters or pagination changes, but only after preferences are loaded
  useEffect(() => {
    if (preferencesLoaded) {
      loadDevices();
    }
  }, [preferencesLoaded, searchTerm, modelFilter, statusFilter, sortBy, sortOrder, currentPage, itemsPerPage, loadDevices]);

  // Pagination handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };



  const getWarrantyStatus = (warrantyExpires: string | null): { status: string; color: string } => {
    if (!warrantyExpires) return { status: 'No Info', color: 'text-gray-500' };
    
    const now = new Date();
    const warrantyDate = new Date(warrantyExpires);
    const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    
    if (warrantyDate <= now) {
      return { status: 'Expired', color: 'text-red-600' };
    } else if (warrantyDate <= thirtyDaysFromNow) {
      return { status: 'Expiring Soon', color: 'text-yellow-600' };
    } else {
      return { status: 'Active', color: 'text-green-600' };
    }
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  // Define table columns
  const columns: TableColumn<Device>[] = [
    {
      id: 'model',
      label: 'Device Model',
      width: 120,
      visible: true,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: deviceModels.map(model => ({ value: model, label: model })),
      render: (device) => (
        <div className="text-sm min-w-0">
          <div className="font-medium text-gray-900 dark:text-white truncate" title={device.model}>
            {device.model}
          </div>
          <div className="text-gray-500 dark:text-gray-400 text-xs font-mono truncate" title={device.serialNumber}>
            {device.serialNumber}
          </div>
        </div>
      ),
    },
    {
      id: 'customerName',
      label: 'Customer',
      width: 200,
      visible: true,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (device) => (
        <div className="text-sm min-w-0">
          <div className="font-medium text-gray-900 dark:text-white truncate" title={device.customerName}>
            {device.customerName}
          </div>
          <div className="text-gray-500 dark:text-gray-400 text-xs truncate" title={device.customerEmail}>
            {device.customerEmail}
          </div>
        </div>
      ),
    },
    {
      id: 'companyName',
      label: 'Company',
      width: 180,
      visible: true,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (device) => (
        <div className="text-sm min-w-0">
          <div className="font-medium text-gray-900 dark:text-white truncate" title={device.companyName || 'No Company'}>
            {device.companyName || 'No Company'}
          </div>
        </div>
      ),
    },
    {
      id: 'country',
      label: 'Dataset Origin',
      width: 130,
      visible: true,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'select',
      render: (device) => (
        <div className="text-sm min-w-0">
          <div className="text-gray-900 dark:text-white truncate" title={device.country || '-'}>
            {device.country || '-'}
          </div>
        </div>
      ),
    },
    {
      id: 'ticketCount',
      label: 'Tickets',
      width: 100,
      visible: true,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'number',
      render: (device) => (
        <div className="text-sm text-center">
          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
            device.ticketCount > 0 
              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200' 
              : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
          }`}>
            {device.ticketCount}
          </span>
        </div>
      ),
    },
    {
      id: 'serviceCount',
      label: 'Services',
      width: 100,
      visible: true,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'number',
      render: (device) => (
        <div className="text-sm text-center">
          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
            device.serviceCount > 0 
              ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200' 
              : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
          }`}>
            {device.serviceCount}
          </span>
        </div>
      ),
    },
    {
      id: 'lastTicketDate',
      label: 'Last Ticket',
      width: 120,
      visible: true,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'date',
      render: (device) => {
        const formatDate = (dateString: string | null) => {
          if (!dateString) return 'Never';
          try {
            const date = new Date(dateString);
            return date.toLocaleDateString();
          } catch {
            return 'Invalid Date';
          }
        };

        const formatRelativeTime = (dateString: string | null) => {
          if (!dateString) return 'Never';
          try {
            const date = new Date(dateString);
            const now = new Date();
            const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
            
            if (diffInHours < 24) {
              return `${diffInHours}h ago`;
            } else {
              const diffInDays = Math.floor(diffInHours / 24);
              return `${diffInDays}d ago`;
            }
          } catch {
            return 'Invalid Date';
          }
        };

        return (
          <div className="text-sm">
            <div className="text-gray-900 dark:text-white">
              {formatDate(device.lastTicketDate)}
            </div>
            <div className="text-gray-500 dark:text-gray-400 text-xs">
              {formatRelativeTime(device.lastTicketDate)}
            </div>
          </div>
        );
      },
    },
    {
      id: 'warrantyExpires',
      label: 'Warranty Status',
      width: 140,
      visible: true,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { value: 'active', label: 'Active' },
        { value: 'expired', label: 'Expired' },
        { value: 'expiring-soon', label: 'Expiring Soon' },
        { value: 'no-warranty', label: 'No Info' }
      ],
      render: (device) => {
        const { status, color } = getWarrantyStatus(device.warrantyExpires);
        return (
          <div className="text-sm">
            <div className={`font-medium ${color}`}>
              {status}
            </div>
            <div className="text-gray-500 dark:text-gray-400 text-xs">
              {formatDate(device.warrantyExpires)}
            </div>
          </div>
        );
      },
    },
    {
      id: 'invoiceNumber',
      label: 'Invoice',
      width: 130,
      visible: true,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (device) => (
        <div className="text-sm">
          <div className="text-gray-900 dark:text-white font-mono">
            {device.invoiceNumber || '-'}
          </div>
          <div className="text-gray-500 dark:text-gray-400 text-xs">
            {formatDate(device.invoiceDate)}
          </div>
        </div>
      ),
    },
    {
      id: 'createdAt',
      label: 'Created',
      width: 120,
      visible: false,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'date',
      render: (device) => (
        <div className="text-sm text-gray-900 dark:text-white">
          {formatDate(device.createdAt)}
        </div>
      ),
    },

  ];



  // Define actions
  const actions = [
    {
      label: 'View Details',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
      ),
      onClick: onDeviceSelect,
      className: 'text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300',
    },
    {
      label: 'Edit Device',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      ),
      onClick: (device: Device) => {
        // TODO: Implement edit functionality
        console.log('Edit device:', device.id);
      },
      className: 'text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300',
    },
    {
      label: 'Delete Device',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      ),
      onClick: async (device: Device) => {
        if (window.confirm(`Are you sure you want to delete device ${device.model} - ${device.serialNumber}?\n\nThis action cannot be undone.`)) {
          try {
            await api.deleteDevice(device.id);
            // Reload the devices list to reflect the change
            await loadDevices();
            // You could also show a success message here if desired
          } catch (error) {
            console.error('Error deleting device:', error);
            alert(`Failed to delete device: ${error instanceof Error ? error.message : 'Unknown error'}`);
          }
        }
      },
      className: 'text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300',
    },
  ];

  // Export function
  const handleExport = () => {
    const exportData = devices.map(device => {
      const warranty = getWarrantyStatus(device.warrantyExpires);
      return {
        'Device Model': device.model,
        'Serial Number': device.serialNumber,
        'Customer Name': device.customerName,
        'Customer Email': device.customerEmail,
        'Company Name': device.companyName || 'No Company',
        'Dataset Origin': device.country || '-',
        'Ticket Count': device.ticketCount,
        'Service Count': device.serviceCount,
        'Last Ticket Date': formatDate(device.lastTicketDate),
        'Warranty Status': warranty.status,
        'Warranty Expires': formatDate(device.warrantyExpires),
        'Invoice Number': device.invoiceNumber || '-',
        'Invoice Date': formatDate(device.invoiceDate),
        'Comments': device.comments || '-',
        'Added Date': formatDate(device.createdAt),
      };
    });

    const XLSX = require('xlsx');
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Devices');
    
    const filename = `devices-${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error loading devices</h3>
            <p className="mt-2 text-sm text-red-700 dark:text-red-300">{error}</p>
            <button
              onClick={loadDevices}
              className="mt-2 bg-red-100 hover:bg-red-200 dark:bg-red-800 dark:hover:bg-red-700 px-3 py-1 rounded text-sm text-red-800 dark:text-red-200"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700 dark:text-red-200">{error}</p>
            </div>
          </div>
        </div>
      )}

     {/* Search Section */}
<div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
    <div className="flex-1 max-w-md">
      <label htmlFor="device-search" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        Search Devices
      </label>
      <EnhancedSearchInput
        value={searchTerm}
        onChange={handleSearchChange}
        placeholder="Search by model, serial, invoice #, customer name, email, company, country... (use &quot;quotes&quot; for exact matches)"
        showSearchTips={true}
        debounceMs={300}
      />
    </div>
    <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
      <span>{totalItems} devices found</span>
    </div>
  </div>
</div>

      <ConfigurableTable
        data={devices}
        columns={columns}
        loading={loading}
        storageKey="devices-table"
        title="Devices"
        searchTerm=""
        onSearchChange={undefined}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        onRowClick={onDeviceSelect}
        onExport={handleExport}
        exportFilename="devices"
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortChange={(newSortBy, newSortOrder) => {
          setSortBy(newSortBy);
          setSortOrder(newSortOrder);
          setCurrentPage(1); // Reset to first page when sorting changes
        }}
        actions={actions}
        emptyState={
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No devices found</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {searchTerm || columnFilters.length > 0 ? 'Try adjusting your search or filters.' : 'No devices to display.'}
            </p>
          </div>
        }
      />

      {/* Pagination Controls - Now positioned below the table */}
      {totalItems > 0 && (
        <div className="flex items-center justify-between px-6 py-3 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 rounded-b-md">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-700 dark:text-gray-300">Items per page:</span>
              <select
                value={itemsPerPage}
                onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                className="border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
            
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Showing {Math.min(((currentPage - 1) * itemsPerPage) + 1, totalItems)} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} entries
            </span>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage <= 1}
              className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
            >
              Previous
            </button>
            
            {/* Page numbers */}
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const pageNumber = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
              return (
                <button
                  key={pageNumber}
                  onClick={() => handlePageChange(pageNumber)}
                  className={`px-3 py-1 text-sm border rounded-md ${
                    pageNumber === currentPage
                      ? 'bg-blue-500 text-white border-blue-500'
                      : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  {pageNumber}
                </button>
              );
            })}
            
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeviceList;